import { useEffect, useMemo, useState } from 'react';
import { useResource } from '../hooks/useResource';
import { api, type ProactiveChannel, type StartConversationInput } from '../lib/api';
import type { Inbox } from '../lib/types';

// Agent-initiated outbound. Backend (POST /agent/proactive) only supports the
// non-web channels below — web chats are inbound-only, so we don't offer 'web'.
const CHANNELS: Array<{ value: ProactiveChannel; label: string; idLabel: string; placeholder: string }> = [
  { value: 'whatsapp', label: 'WhatsApp', idLabel: 'Phone (E.164)', placeholder: '+15551234567' },
  { value: 'sms', label: 'SMS', idLabel: 'Phone (E.164)', placeholder: '+15551234567' },
  { value: 'email', label: 'Email', idLabel: 'Email address', placeholder: 'name@example.com' },
  { value: 'facebook', label: 'Facebook', idLabel: 'Facebook PSID', placeholder: '1234567890123456' },
];

const INTENT_CATEGORIES: Array<{ value: StartConversationInput['intent']['category']; label: string }> = [
  { value: 'service_reply', label: 'Service reply' },
  { value: 'utility', label: 'Utility' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'transactional', label: 'Transactional' },
];

function genIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `proactive-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function NewConversationModal({
  tenantCode,
  agentUid,
  prefill,
  onClose,
  onCreated,
}: {
  tenantCode: string;
  agentUid: string;
  prefill?: { name?: string; email?: string; phone?: string };
  onClose: () => void;
  onCreated: (chatId: string) => void;
}) {
  const { items: inboxes, loading: inboxesLoading } = useResource<Inbox>('inboxes');

  // Pick a sensible default channel from the prefill (email -> email, else whatsapp).
  const initialChannel: ProactiveChannel = prefill?.email && !prefill?.phone ? 'email' : 'whatsapp';
  const [inboxId, setInboxId] = useState('');
  const [channel, setChannel] = useState<ProactiveChannel>(initialChannel);
  const [identifier, setIdentifier] = useState(
    initialChannel === 'email' ? prefill?.email ?? '' : prefill?.phone ?? ''
  );
  const [displayName, setDisplayName] = useState(prefill?.name ?? '');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<StartConversationInput['intent']['category']>('service_reply');
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateLang, setTemplateLang] = useState('en');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const channelMeta = useMemo(() => CHANNELS.find((c) => c.value === channel)!, [channel]);

  // Default to the first inbox once they load.
  useEffect(() => {
    if (!inboxId && inboxes.length > 0) setInboxId(inboxes[0].id);
  }, [inboxId, inboxes]);

  function buildTo(): StartConversationInput['to'] | null {
    const v = identifier.trim();
    if (!v) return null;
    if (channel === 'email') return { email: v };
    if (channel === 'facebook') return { psid: v };
    return { phone: v }; // whatsapp / sms
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!inboxId) {
      setErr('Pick an inbox.');
      return;
    }
    const to = buildTo();
    if (!to) {
      setErr(`Enter the ${channelMeta.idLabel.toLowerCase()}.`);
      return;
    }
    if (!content.trim()) {
      setErr('Enter a first message.');
      return;
    }
    if (channel === 'email' && !subject.trim()) {
      setErr('Enter an email subject.');
      return;
    }
    if (useTemplate && !templateName.trim()) {
      setErr('Template name is required when sending a template.');
      return;
    }

    const input: StartConversationInput = {
      tenantCode,
      agentUid,
      inboxId,
      channel,
      to,
      intent: { category, initiatedBy: 'agent' },
      content: content.trim(),
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      ...(channel === 'email' && subject.trim() ? { subject: subject.trim() } : {}),
      idempotencyKey: genIdempotencyKey(),
      ...(useTemplate
        ? { template: { name: templateName.trim(), language: templateLang.trim() || 'en' } }
        : {}),
    };

    setBusy(true);
    try {
      const res = await api.startConversation(input);
      if (res.status === 'sent' && res.chatId) {
        onCreated(res.chatId);
        return;
      }
      // 'denied' / 'failed' — surface the policy/consent reason inline.
      setErr(
        res.reason
          ? `Send ${res.status}: ${res.reason}`
          : `Send ${res.status}. The outbound policy or consent check blocked this message.`
      );
    } catch (e) {
      // authedFetch throws "STATUS body" on non-2xx; try to pull a reason out.
      const msg = e instanceof Error ? e.message : String(e);
      let reason = msg;
      const jsonStart = msg.indexOf('{');
      if (jsonStart >= 0) {
        try {
          const parsed = JSON.parse(msg.slice(jsonStart)) as { reason?: string; status?: string; error?: unknown };
          reason = parsed.reason || (typeof parsed.error === 'string' ? parsed.error : msg);
        } catch {
          /* keep raw */
        }
      }
      setErr(reason);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold mb-3">New conversation</div>
        <form onSubmit={submit} className="space-y-2.5">
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Inbox</label>
            <select
              value={inboxId}
              onChange={(e) => setInboxId(e.target.value)}
              disabled={inboxesLoading}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {inboxes.length === 0 && <option value="">{inboxesLoading ? 'Loading…' : 'No inboxes'}</option>}
              {inboxes.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Channel</label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value as ProactiveChannel)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">{channelMeta.idLabel}</label>
            <input
              type={channel === 'email' ? 'email' : 'text'}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={channelMeta.placeholder}
              dir="ltr"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Display name (optional)</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Intent category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as StartConversationInput['intent']['category'])}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {INTENT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {channel === 'email' && (
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject line"
                dir="auto"
                maxLength={200}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">First message</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              maxLength={10000}
              dir="auto"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              required
            />
          </div>

          {channel !== 'email' && (
            <div className="border border-gray-200 rounded p-2 space-y-2">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={useTemplate}
                  onChange={(e) => setUseTemplate(e.target.checked)}
                />
                Send as approved template (required outside the messaging window)
              </label>
              {useTemplate && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="template_name"
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <input
                    type="text"
                    value={templateLang}
                    onChange={(e) => setTemplateLang(e.target.value)}
                    placeholder="en"
                    className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
              )}
            </div>
          )}

          {err && <div className="text-xs text-red-600 whitespace-pre-wrap">{err}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Start conversation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
